import { loadConnectAndInitialize } from "@stripe/connect-js"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQuery } from "react-query"

import Button from "src/components/Button"
import Form from "src/components/Form"
import HeadingPageTitle from "src/components/Heading/HeadingPageTitle"
import Loader from "src/components/Loader"
import StripeConnectContainer from "src/components/StripeConnect/StripeConnectContainer"

import { createAccountSession } from "src/api/StripeConnect"

import { useTracker } from "src/hooks/use_tracker"

const StripeOnboardingStep = ({
  onNext,
  onSkip,
  stripeManagedAccountId,
  smaCountries,
}) => {
  const tracker = useTracker()
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null)
  const [currentAccountId, setCurrentAccountId] = useState(
    stripeManagedAccountId
  )

  const { isLoading: isSessionLoading, error: sessionError } = useQuery(
    ["stripeAccountSession", currentAccountId],
    () => createAccountSession({ stripeAccountId: currentAccountId }),
    {
      enabled: !!currentAccountId,
      onSuccess: async (response) => {
        const stripeConnect = await loadConnectAndInitialize({
          publishableKey: DockwaConfig.STRIPE_PUBLISHABLE_KEY,
          fetchClientSecret: () => Promise.resolve(response.client_secret),
          setCollectionOptions: {
            fields: "currently_due",
            futureRequirements: "include",
          },
          appearance: {
            variables: {
              colorPrimary: "#1e4acb",
            },
          },
        })

        setStripeConnectInstance(stripeConnect)
      },
    }
  )

  const handleAccountCreated = (accountId) => {
    setCurrentAccountId(accountId)
  }

  const handleSuccess = () => {
    tracker.trackEvent("onboarding:claim:stripe_onboarding_setup_completed")
    onNext()
  }

  const handleSkip = () => {
    tracker.trackEvent("onboarding:claim:stripe_onboarding_setup_skipped")
    onSkip()
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <div className="mb-4 flex w-full flex-col items-center justify-center px-2 text-center lg:w-1/3">
        <div className="flex flex-col space-y-2 text-center">
          <HeadingPageTitle>Set Up Payment Processing</HeadingPageTitle>
          <p className="text-gray-600">
            Enable secure payment processing through Stripe in order to accept
            bookings and payments from boaters.
          </p>
        </div>
        {isSessionLoading ? (
          <Loader />
        ) : sessionError ? (
          <Form.Error>
            {sessionError.message ||
              "Failed to load payment processing setup. Please try again."}
          </Form.Error>
        ) : (
          <StripeConnectContainer
            stripeManagedAccountId={currentAccountId}
            smaCountries={smaCountries}
            onSuccess={handleSuccess}
            onAccountCreated={handleAccountCreated}
            stripeConnectInstance={stripeConnectInstance}
          />
        )}
        <div className="mt-6 text-center">
          <Button variant="secondary" onClick={handleSkip}>
            Skip for now
          </Button>
          <p className="mt-2 text-xs text-gray-500">
            You can set this up later in your settings
          </p>
        </div>
      </div>
    </div>
  )
}

StripeOnboardingStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  stripeManagedAccountId: PropTypes.string,
  smaCountries: PropTypes.arrayOf(
    PropTypes.shape({
      countryCode: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
}

export default StripeOnboardingStep
